<template>
  <div class="sign-up">
    <div class="signup-title">Sign up for Free!</div>
    <div class="input-wrapper">
      <input
        type="text"
        class="input-box"
        id="userEmail"
        placeholder="Enter your email"
        v-model="userEmail"
        @change="onEmailChange"
        @blur="onEmailChange"
      />
      <span class="error-wrapper" v-if="emailErrorMsg !== ''">{{
        emailErrorMsg
      }}</span>
    </div>
    <div class="input-wrapper">
      <input
        type="password"
        class="input-box"
        id="userPassword"
        placeholder="Create a Password"
        v-model="userPassword"
        @change="onPasswordChange"
        @blur="onPasswordChange"
      />
      <span class="error-wrapper" v-if="passwordErrorMsg !== ''">
        {{ passwordErrorMsg }}
      </span>
    </div>
    <div class="error-wrapper" v-if="limitExceed">
      We have noticed too many registrations from this domain. Please contact
      <a target="_blank" rel="nofollow" href="mailto:support@adapt.io"
        >support</a
      >
      for further help
    </div>
    <div class="error-wrapper" v-if="businessEmail">
      Please enter your work email
    </div>
    <div class="error-wrapper" v-if="invalidEmail">Invalid email address</div>
    <div class="error-wrapper" v-if="emailAlreadyRegistered">
      Email is already registered
    </div>
    <div class="error-wrapper" v-if="emailAlreadyLinked">
      The email address you have entered is linked with another account. Please
      <a href="mailto:support@adapt.io">contact us</a> for more details.
    </div>
    <div>
      <button class="action-btn" @click="userRegister">
        <template v-if="!loading">GET STARTED</template>
        <Spinner v-else />
      </button>
    </div>

    <div class="switch-section">
      Already have an account?
      <span class="anchor-link" @click="setShowSignIn">Sign In</span>
    </div>
    <div class="privacy-policy" v-if="!euTimeZone">
      By signing up, you are accepting our
      <a :href="termsLink" class="terms-link" target="_blank">terms</a>
      and
      <a :href="privacyLink" class="privacy-link" target="_blank"
        >privacy policy</a
      >.
    </div>
    <div class="privacy-policy privacy-policy-eu" v-if="euTimeZone">
      <div class="checkbox-error">
        <div class="error" :class="{ show: showTermConditionError }">
          Please accept our terms and privacy policy
        </div>
        <div class="error" :class="{ show: showStorePersonalError }">
          Please give your consent to store information
        </div>
      </div>
      <div class="termCondition checkbox-container">
        <div class="checkbox-wrap">
          <input
            type="checkbox"
            id="termCondition"
            :value="termCondition"
            v-on:change="termCondition = !termCondition"
          />
        </div>
        <div class="checkbox-content">
          By signing up, you are accepting our
          <a :href="termsLink" class="terms-link" target="_blank">terms</a>
          and
          <a :href="privacyLink" class="privacy-link" target="_blank"
            >privacy policy</a
          >.
        </div>
      </div>
      <div class="storePersonal checkbox-container">
        <div class="checkbox-wrap">
          <input
            type="checkbox"
            id="storePersonal"
            :value="storePersonal"
            v-on:change="storePersonal = !storePersonal"
          />
        </div>
        <div class="checkbox-content">
          I give my consent to store my personal information outside the
          European Union.
        </div>
      </div>
      <div class="productAnnouncement checkbox-container">
        <div class="checkbox-wrap">
          <input
            type="checkbox"
            id="productAnnouncement"
            :value="productAnnouncement"
            v-on:change="emailPreferencesOnChange"
          />
        </div>
        <div class="checkbox-content">
          I would like to receive email updates from Adapt on new product
          announcements and upgrades.
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { BASE_URL, URLS } from '@/assets/util/constants/constants.js'
import Spinner from '@/components/Spinner'
import sharedFunction from './../../assets/util/functions.js'
import { MxEvent } from './../../assets/util/constants/analytics.js'
import {
  validateEUTimeZone,
  getLocationData,
  getUsersTimeZone
} from '@/assets/util/commonUtil.js'
import { post } from '@/assets/util/rest.js'

export default {
  name: 'SignUpView',
  props: [
    'showPopup',
    'validateEmail',
    'validatePassword',
    'setShowSignIn',
    'connectWithGoogleAuth',
    'authErrorMsg',
    'googleAuthLoading'
  ],
  components: {
    Spinner
  },
  data: function() {
    return {
      termsLink: BASE_URL + '/termsAndConditions.htm',
      privacyLink: BASE_URL + '/privacy.htm',
      userEmail: '',
      userPassword: '',
      emailErrorMsg: '',
      passwordErrorMsg: '',
      euTimeZone: validateEUTimeZone() || false,
      termCondition: false,
      storePersonal: false,
      productAnnouncement: false,
      showTermConditionError: false,
      showStorePersonalError: false,
      triggeredIpinfo: false,
      limitExceed: false,
      emailAlreadyRegistered: false,
      emailAlreadyLinked: false,
      businessEmail: false,
      invalidEmail: false,
      loading: false
    }
  },
  methods: {
    checkAndTriggerIpInfo() {
      if (!this.triggeredIpinfo) {
        getLocationData()
        this.triggeredIpinfo = true
      }
    },
    onEmailChange() {
      this.emailErrorMsg = this.validateEmail(this.userEmail)
      this.checkAndTriggerIpInfo()
    },
    onPasswordChange() {
      this.passwordErrorMsg = this.validatePassword(this.userPassword)
      this.checkAndTriggerIpInfo()
    },
    emailPreferencesOnChange() {
      this.productAnnouncement = !this.productAnnouncement
    },
    fieldValidationOnlyEu() {
      if (this.euTimeZone) {
        if (!this.termCondition) {
          this.showTermConditionError = true
        } else {
          this.showTermConditionError = false
        }
        if (!this.storePersonal) {
          this.showStorePersonalError = true
        } else {
          this.showStorePersonalError = false
        }
        if (this.termCondition && this.storePersonal) return true
        else return false
      } else {
        return true
      }
    },
    fieldValidation() {
      if (this.emailErrorMsg === '' && this.passwordErrorMsg === '') {
        return this.fieldValidationOnlyEu()
      }
      return false
    },
    resetDefaultErrorState() {
      this.limitExceed = false
      this.emailAlreadyRegistered = false
      this.emailAlreadyLinked = false
      this.businessEmail = false
      this.invalidEmail = false
      this.showTermConditionError = false
      this.showStorePersonalError = false
    },
    mixpanelDataForRegistration() {
      const dataMap = {}
      dataMap.userEmail = this.userEmail
      dataMap.mode = 'Email'
      dataMap.userType = 'FREE'
      dataMap.pageType = ''
      return dataMap
    },
    connectWithGoogleAuthSignUp() {
      if (this.fieldValidationOnlyEu()) {
        this.connectWithGoogleAuth(
          this.euTimeZone ? this.productAnnouncement : true
        )
      }
    },
    userRegister() {
      this.emailErrorMsg = this.validateEmail(this.userEmail)
      this.passwordErrorMsg = this.validatePassword(this.userPassword)
      this.resetDefaultErrorState()
      if (this.fieldValidation()) {
        this.loading = true
        const userData = {}
        userData.email = this.userEmail.toLowerCase()
        userData.password = this.userPassword
        userData.source = 'plugin-li'
        userData.userTimezone = getUsersTimeZone()
        userData.pageSource = 'Plugin Page'
        userData.emailPreferences = this.euTimeZone
          ? this.productAnnouncement
          : true
        userData.mixpanelInfo = this.mixpanelDataForRegistration()
        userData.country = localStorage.getItem('country')
        userData.state = localStorage.getItem('state')
        userData.city = localStorage.getItem('city')
        let mixpanelData = null

        if (window.mixpanel) {
          mixpanelData = window.mixpanel.persistence.properties()
          const params = new URLSearchParams(window.location.search)
          if (params.has('utm_medium'))
            mixpanelData.utm_medium = params.get('utm_medium')
          if (params.has('utm_source'))
            mixpanelData.utm_source = params.get('utm_source')

          userData.mixpanelData = mixpanelData
        }

        post(URLS.registerUser, userData)
          .then(res => {
            res.json().then(response => {
              if (response == 'true') {
                window.location.reload()
              } else if (response == 'limit') {
                this.limitExceed = true
                sharedFunction.track(MxEvent.signUpLimitError, userData)
              } else if (response == 'business') {
                this.businessEmail = true
                sharedFunction.track(
                  MxEvent.signUpNonBusinessEmailError,
                  userData
                )
              } else if (response == 'invalid') {
                this.invalidEmail = true
              } else if (response == 'exist_linked_with_another_account') {
                this.emailAlreadyLinked = true
              } else {
                this.emailAlreadyRegistered = true
              }
              this.loading = false
            })
          })
          .catch(() => {
            this.loading = false
          })
      }
    }
  }
}
</script>

<style scoped lang="scss">
.signup-title {
  padding: 10px 0 20px;
  text-align: center;
  color: #444;
}
.privacy-policy.privacy-policy-eu {
  width: 100% !important;
  margin-top: 0px !important;
  .checkbox-error {
    text-align: left;
    .error {
      position: relative;
      padding-left: 16px;
      display: block;
      font-size: 10px;
      line-height: 1.3;
      color: red;
      visibility: hidden;
    }
    .error.show {
      visibility: visible;
    }
  }
  .checkbox-container {
    display: flex;
    text-align: left;
    margin-bottom: 6px;
    .checkbox-wrap {
      margin-right: 10px;
      margin-top: -1px;
    }
  }
}
</style>
